<template>
  <div class="custom-node source-node">
    <div class="node-header">
      <div class="node-icon">
        <Database :size="20" />
      </div>
      <div class="node-info">
        <span class="node-type">Source</span>
        <span class="node-label">{{ data.label || "Data Source" }}</span>
      </div>
      <button @click="$emit('delete')" class="node-delete">
        <X :size="14" />
      </button>
    </div>

    <div class="node-body">
      <div v-if="data.endpoint" class="node-detail">
        <span class="detail-label">Endpoint:</span>
        <span class="detail-value">{{ data.endpoint }}</span>
      </div>
      <div v-if="data.sampleColumns?.length" class="node-detail">
        <span class="detail-label">Columns:</span>
        <span class="detail-value">{{ data.sampleColumns.length }} columns</span>
      </div>
      <div v-if="!data.connectionId" class="node-placeholder">Click to configure</div>
    </div>

    <div v-if="data.connectionId" class="node-status">
      <div class="status-dot configured"></div>
      <span>Connected</span>
    </div>

    <Handle type="source" :position="Position.Right" class="node-handle" />
  </div>
</template>

<script setup>
import { Handle, Position } from "@vue-flow/core";
import { Database, X } from "lucide-vue-next";

defineProps({
  data: {
    type: Object,
    required: true,
  },
});

defineEmits(["delete"]);
</script>

<style scoped>
.custom-node {
  background: white;
  border: 2px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  min-width: 280px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.custom-node:hover {
  border-color: var(--color-accent);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.source-node {
  border-left: 4px solid #667eea;
}

.node-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.node-icon {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.node-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.node-type {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-secondary);
  font-weight: 600;
}

.node-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.node-delete {
  background: transparent;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s;
  flex-shrink: 0;
}

.node-delete:hover {
  background: rgba(220, 38, 38, 0.1);
  color: var(--color-error);
}

.node-body {
  padding: 1rem;
}

.node-detail {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.node-detail:last-child {
  margin-bottom: 0;
}

.detail-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--color-text-secondary);
  font-weight: 600;
}

.detail-value {
  font-size: 13px;
  color: var(--color-text-primary);
  font-family: "Monaco", "Consolas", monospace;
}

.node-placeholder {
  text-align: center;
  padding: 1rem 0;
  color: var(--color-text-secondary);
  font-size: 13px;
  font-style: italic;
}

.node-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: rgba(102, 126, 234, 0.05);
  border-top: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 12px;
  color: #667eea;
  font-weight: 600;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #667eea;
  box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.95);
  }
}

.node-handle {
  width: 12px;
  height: 12px;
  background: white;
  border: 2px solid #667eea;
  transition: all 0.2s;
}

.node-handle:hover {
  width: 16px;
  height: 16px;
  background: #667eea;
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.2);
}

.node-handle.source {
  right: -6px;
}
</style>
